/**
 * Accordion Tabs (DRIVEN custom)
 *
 * Extracted from the inline <script> that previously lived inside
 * sections/product-details.liquid so the block can be maintained without
 * editing vendor theme files.
 *
 * Configuration is read from a <script type="application/json"
 * data-accordion-tabs-config> tag rendered inside each .accordion-tabs
 * container by snippets/accordion-tabs.liquid.
 *
 * All DOM lookups are scoped to the container element rather than to
 * document + [data-product-id], so multiple instances of the same product on
 * one page (quick view, featured product) no longer collide.
 */
(function () {
  'use strict';

  var ROWS_PER_PAGE = 10;

  /** Current layout mode for a container: 'horizontal' or 'accordion'. */
  function getMode(container) {
    if (container.classList.contains('tabs-horizontal')) return 'horizontal';
    if (container.classList.contains('accordion')) return 'accordion';
    if (container.classList.contains('responsive')) {
      return window.innerWidth >= 769 ? 'horizontal' : 'accordion';
    }
    return 'horizontal';
  }

  /**
   * Paginate a fitments table 10 rows at a time.
   * Guarded so repeated calls (e.g. re-clicking the Fitments tab) reuse the
   * existing pagination instead of stacking duplicate button listeners.
   */
  function initPagination(container) {
    if (container.dataset.paginationInit === 'true') return;

    var table = container.querySelector('table');
    var paginationControls = container.querySelector('.pagination-controls');

    if (!table) {
      if (paginationControls) paginationControls.style.display = 'none';
      return;
    }

    container.dataset.paginationInit = 'true';
    table.classList.add('fitments-table');

    var rows = table.querySelectorAll('tr');
    var headerRow = table.querySelector('tr:has(th)') || rows[0];
    var dataRows = Array.prototype.filter.call(rows, function (row) {
      return row !== headerRow;
    });
    var currentPage = 1;
    var totalPages = Math.ceil(dataRows.length / ROWS_PER_PAGE);

    if (headerRow) headerRow.classList.add('header-row', 'visible');

    if (dataRows.length === 0) {
      if (paginationControls) paginationControls.style.display = 'none';
      return;
    }

    var prevButton = container.querySelector('.prev-page');
    var nextButton = container.querySelector('.next-page');
    var pageIndicator = container.querySelector('.page-indicator');

    function updatePagination() {
      dataRows.forEach(function (row) {
        row.classList.remove('visible');
      });
      var start = (currentPage - 1) * ROWS_PER_PAGE;
      dataRows.slice(start, start + ROWS_PER_PAGE).forEach(function (row) {
        row.classList.add('visible');
      });

      if (prevButton) prevButton.disabled = currentPage === 1;
      if (nextButton) nextButton.disabled = currentPage === totalPages;
      if (pageIndicator) {
        pageIndicator.textContent = 'Page ' + currentPage + ' of ' + totalPages;
      }
    }

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        if (currentPage > 1) {
          currentPage--;
          updatePagination();
        }
      });
    }
    if (nextButton) {
      nextButton.addEventListener('click', function () {
        if (currentPage < totalPages) {
          currentPage++;
          updatePagination();
        }
      });
    }

    updatePagination();
  }

  /** Activate the tab identified by tabId within this container. */
  function activateTab(container, thumb, tabId) {
    container
      .querySelectorAll('.tab-thumb, .tab-item, .tab-panel')
      .forEach(function (sibling) {
        sibling.classList.remove('is-active');
      });
    container.querySelectorAll('.tab-thumb').forEach(function (sibling) {
      sibling.setAttribute('aria-selected', 'false');
    });

    thumb.classList.add('is-active');
    thumb.setAttribute('aria-selected', 'true');

    var panel = container.querySelector('#tab-content-' + CSS.escape(tabId));
    if (panel) {
      panel.classList.add('is-active');
      var tabItem = panel.closest('.tab-item');
      if (tabItem) tabItem.classList.add('is-active');
    }

    var mode = getMode(container);
    var otherThumbSelector =
      mode === 'horizontal'
        ? '.tab-item .tab-thumb[data-tab-id="' + tabId + '"]'
        : '.tab-controls .tab-thumb[data-tab-id="' + tabId + '"]';
    var otherThumb = container.querySelector(otherThumbSelector);
    if (otherThumb && otherThumb.style.display !== 'none') {
      otherThumb.classList.add('is-active');
      otherThumb.setAttribute('aria-selected', 'true');
    }

    return panel;
  }

  function setupContainer(container) {
    if (container.dataset.accordionTabsInit === 'true') return;
    container.dataset.accordionTabsInit = 'true';

    var configEl = container.querySelector(
      'script[type="application/json"][data-accordion-tabs-config]'
    );
    var config = { variantMetafields: {}, initialVariantId: null };
    if (configEl) {
      try {
        config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('accordion-tabs: could not parse config', e);
      }
    }
    var variantMetafields = config.variantMetafields || {};

    container.querySelectorAll('.tab-thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();

        var mode = getMode(container);
        var tabId = thumb.getAttribute('data-tab-id');

        if (
          mode === 'horizontal' &&
          thumb.closest('.tab-item') &&
          !window.matchMedia('(max-width: 768px)').matches
        ) {
          return;
        }
        if (mode === 'accordion' && thumb.closest('.tab-controls')) return;

        var panel = activateTab(container, thumb, tabId);

        // Scroll only if the thumb is not already fully in view.
        var rect = thumb.getBoundingClientRect();
        var isInView = rect.top >= 0 && rect.bottom <= window.innerHeight;
        if (!isInView) {
          var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          window.scrollTo({ top: rect.top + scrollTop - 20, behavior: 'smooth' });
        }

        if (panel) {
          var fitments = panel.querySelector('.fitments-table-container');
          if (fitments) initPagination(fitments);
        }
      });
    });

    /** Swap per-variant tab content, hiding tabs with no value for the variant. */
    function updateVariantContent(variantId) {
      var metafields = variantMetafields[variantId] || {};

      container.querySelectorAll('.tab-panel').forEach(function (panel) {
        var tabId = panel.id.replace('tab-content-', '');
        var metafieldData = metafields[tabId];
        if (!metafieldData) return;

        var thumb = container.querySelector(
          '.tab-controls .tab-thumb[data-tab-id="' + tabId + '"]'
        );
        var accordionThumb = container.querySelector(
          '.tab-item .tab-thumb[data-tab-id="' + tabId + '"]'
        );
        var tabItem = accordionThumb ? accordionThumb.closest('.tab-item') : null;

        if (metafieldData.value) {
          if (thumb) thumb.style.display = '';
          if (accordionThumb) accordionThumb.style.display = '';
          if (tabItem) tabItem.style.display = '';
          panel.innerHTML = metafieldData.value;
        } else {
          if (thumb) thumb.style.display = 'none';
          if (accordionThumb) accordionThumb.style.display = 'none';
          if (tabItem) tabItem.style.display = 'none';
          if (panel.classList.contains('is-active')) {
            panel.classList.remove('is-active');
            var firstVisibleThumb =
              container.querySelector(
                '.tab-controls .tab-thumb:not([style*="display: none"])'
              ) ||
              container.querySelector(
                '.tab-item .tab-thumb:not([style*="display: none"])'
              );
            if (firstVisibleThumb) firstVisibleThumb.click();
          }
        }
      });
    }

    // The variant picker lives in a different section (main-product), so this
    // listener stays on document by necessity.
    document.addEventListener('change', function (e) {
      if (e.target.name === 'id') updateVariantContent(e.target.value);
    });

    var selectedInput = document.querySelector('[name="id"]');
    var initialVariantId =
      (selectedInput && selectedInput.value) || config.initialVariantId;
    if (initialVariantId) updateVariantContent(String(initialVariantId));

    // Paginate any fitments table that is visible on load.
    container
      .querySelectorAll('.fitments-table-container')
      .forEach(function (fitments) {
        var panel = fitments.closest('.tab-panel');
        if (panel && panel.classList.contains('is-active')) {
          initPagination(fitments);
        }
      });
  }

  function init() {
    document.querySelectorAll('.accordion-tabs').forEach(setupContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialise after a theme-editor section reload.
  document.addEventListener('shopify:section:load', init);
})();
